/* eslint-disable complexity */
import {
    ClipboardListIcon,
    InformationCircleIcon,
    NinchatQueueIcon,
} from 'components/Icon'

import {
    NinchatErrorEvent, NinchatSsoBegunEvent,
} from 'api/types/events'

import React, {useContext} from 'react'

import {
    attributesContainer,
    messageContainer,
    messagePrivileged,
    messageTime,
    speechBubble,
    speechBubbleContainer,
    ssoWrapper,
} from '../InfoMessage/styles/InfoMessage'

import {
    useAppDispatch, useAppSelector,
} from 'hooks'


import Attribute from '../InfoMessage/Attribute'
import AudienceMetadata from 'components/AudienceMetadata'
import {AudienceMetadataHeaderView} from 'components/AudienceMetadata/components/AudienceMetadataHeaderView'
import {AudienceMetadata as AudienceMetadataType} from 'components/AudienceMetadata/types'
import Button from 'components/Button'
import {EmbedContext} from 'components/EmbedContext'
import {EmbedEvent} from 'embed/types/events'
import {Icon} from 'components/Icon/types'
import {Metadata} from '../../../api/types/properties'
import {
    MetadataMessageProps,
} from '../types'
import MetadataSSOMessage from './MetadataSSOMessage'

import {MetadataView} from 'components/AudienceMetadata/components/MetadataView'
import {NinchatMessageTypes} from 'api/types/messages'
import {audienceMetadataWrapper} from 'components/AudienceMetadata/styles/AudienceMetadata'
import {beginSsoAuth} from 'api/action/beginSsoAuth'
import {filterAnswers} from 'components/AudienceMetadata/utils/filterAnswers'
import {sendMessage} from 'api/action/sendMessage'
import {styled} from '../../../stitches/config'
import {useSendToParent} from 'embed/utils/event'
import {useTranslation} from 'react-i18next'

const StyledMessageContainer = styled('div', messageContainer)
const StyledSpeechBubbleContainer = styled('div', {
    ...speechBubbleContainer,
    '.compact &': {
        '> div': {margin: 0},
        'background': '$messageMetadataCompactBackground',
        'borderRadius': '6px',
        'padding': '5px',
        'width': '100%',
    },
})
const StyledSpeechBubble = styled('div', speechBubble)
const StyledTime = styled('time', messageTime, {
    alignSelf: 'flex-end',
    marginBottom: '2px',
})

const StyledAudienceMetadata = styled('div', audienceMetadataWrapper)

const StyledAttributesContainer = styled('div', attributesContainer)
const StyledPrivileged = styled('div', messagePrivileged)
const StyledSsoWrapper = styled('div', ssoWrapper)

const MessageData = ({data}: {
    data?: Metadata
}): (React.ReactElement | null) => <>{JSON.stringify(data)}</>

MessageData.defaultProps = {data: undefined}

const MetadataMessage = (props: MetadataMessageProps): React.ReactElement => {
    const {
        channelId,
        currentQueue,
        isMe,
        message,
        time,
    } = props

    const {t} = useTranslation()
    const dispatch = useAppDispatch()
    const sendToParent = useSendToParent()
    const embed = useContext(EmbedContext)
    const isEmbedQueueChannel = embed?.variant === 'queue' || embed?.variant === 'channel'

    const userId = useAppSelector(({ninchat}) => ninchat.user_id)
    const userSso = useAppSelector(({ninchat}) => ninchat.user_sso)

    const isAuthenticated = Object.values(userSso || {})[0]?.metadata

    // const channel = useAppSelector(({ninchat}) => ninchat.user_channels[channelId || ''])
    // const memberMessageMetadata = channel?.member_message_metadata?.[userId || ''] || {}

    const [authDeclined, setAuthDeclined] = React.useState(false)

    // eslint-disable-next-line no-console
    // console.log('member message metadata', memberMessageMetadata)

    const redirectTo = (ssoURL: string) => {
        const targetOrigin = embed?.targetOrigin || ''

        sendToParent({
            event: EmbedEvent.EmbedSSO,
            targetOrigin,
            url: ssoURL,
        })
    }

    const ssoName = Object.keys(currentQueue?.queue_sso || {})[0] || ''
    const isDelegatedSSO = message.data?.sso?.Delegation === 'requested'

    const ssoAuthentication = (authenticate: boolean) => {
        if (authenticate) {
            setAuthDeclined(false)
            const accessUrl = embed?.parentUrl || ''

            dispatch(beginSsoAuth({
                access_hpa: isDelegatedSSO,
                access_type: 'session',
                access_url: `${accessUrl}?sso_access_key=`,
                sso_name: ssoName,
            }))
                .unwrap()
                .then(
                    (event: NinchatSsoBegunEvent) => {
                        if (event.sso_url) {
                            redirectTo(event.sso_url)
                        }
                    },
                )
                .catch(
                    (error: NinchatErrorEvent) => {
                        // eslint-disable-next-line no-console
                        console.log(error)
                    },
                )
        } else {
            // TODO - here no matter if message is fold or not it diseapper after refresh
            dispatch(sendMessage({
                channel_id: channelId,
                message_fold: true,
                message_privileged: true,
                message_recipient_ids: [userId || ''],
                message_type: NinchatMessageTypes.NinchatMetadataMessage,
                payload: [JSON.stringify({data: {sso: {Authentication: 'declined'}}})],
            })).then(() => {
                setAuthDeclined(true)
            })
        }
    }


    return (
        <StyledMessageContainer>
            <StyledSpeechBubbleContainer>
                <StyledSpeechBubble >
                    {!!message.data?.post_answers &&
                        <>
                            <StyledAudienceMetadata lite={true}>
                                <AudienceMetadataHeaderView
                                    Icon={ClipboardListIcon}
                                    text={t('Post-audience questionnaire answers')}
                                    values={{[t('Post-audience questionnaire answers')]: filterAnswers(message.data.post_answers, t)}}
                                />
                                <MetadataView compact={false} level={0} metadata={filterAnswers(message.data.post_answers, t)} metadataType='postAnswers' />
                            </StyledAudienceMetadata>
                            <StyledTime>{time}</StyledTime>
                        </>}
                    {!!message.data?.transfer_comment &&
                        <StyledAttributesContainer>
                            {!!message.message_privileged && !(message.message_recipient_ids || []).length && <StyledPrivileged>{t('Transfer comment (only agents see this)')}</StyledPrivileged>}
                            <Attribute
                                icon={NinchatQueueIcon}
                                text={message.data?.transfer_comment}
                            />
                            <StyledTime>{time}</StyledTime>
                        </StyledAttributesContainer>}
                    {!message.data?.post_answers && !message.data?.transfer_comment &&
                        <>
                            {!message.data?.sso &&
                                <StyledAudienceMetadata lite={true}>
                                    {!!message.data?.secure &&
                                        <AudienceMetadata agentIds={[]} lite={true} metadata={{member_message_metadata: {[message.message_user_id ?? '']: message.data as AudienceMetadataType}}} />}
                                    {!message.data?.secure &&
                                        <>
                                            <AudienceMetadataHeaderView
                                                Icon={InformationCircleIcon as Icon}
                                                text={t('Metadata')}
                                                values={{[t('Metadata')]: message.data as AudienceMetadataType}}
                                            />
                                            <MetadataView compact={true} level={0} metadata={message.data as AudienceMetadataType} metadataType='other' />
                                        </>}
                                </StyledAudienceMetadata>}
                            {!!message.data?.sso &&
                                <MetadataSSOMessage isMe={isMe} {...message} />}
                            <StyledTime>{time}</StyledTime>
                        </>}
                </StyledSpeechBubble>
                {/* eslint-disable-next-line max-len */}
                {isEmbedQueueChannel /* && memberMessageMetadata.sso?.Authentication !== 'declined' */ && message.data?.sso?.Authentication === 'requested' && !isMe && !isAuthenticated && !authDeclined ?
                    <StyledSsoWrapper>
                        <Button onPress={() => ssoAuthentication(false)} text={t('Decline')} />
                        <Button color='filled' onPress={() => ssoAuthentication(true)} text={isDelegatedSSO ? t('Authenticate as delegate') : t('Authenticate')} />
                    </StyledSsoWrapper>
                    : null}
            </StyledSpeechBubbleContainer>
        </StyledMessageContainer>
    )
}

export default MetadataMessage
